import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useClerkAuth';
import {
  fetchQuestionCatalogWithQuestions,
  saveQuestionCatalog,
  updateQuestionTranslations,
  updateAnswerTranslations,
  updateQuestionImage,
  Question,
  QuestionCatalogWithQuestions,
  QuestionAnswer,
} from '@/services/questionCatalogs';
import {
  parseQuestions,
  translateQuestion,
  ParsedQuestion,
} from '@/services/openaiServer';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Save,
  Upload,
  Languages,
  CheckCircle,
  Loader,
  Camera,
  X,
  Edit2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { uploadQuestionImageToCloudinary } from '@/services/cloudinaryQuestions';
import { updateQuestionImageInCatalog } from '@/services/questionCatalogs';

const QuestionEditor = () => {
  const { t } = useTranslation();
  const { catalogId } = useParams<{ catalogId: string }>();
  const navigate = useNavigate();
  const { user, canCreateArticle, isLoading } = useAuth();

  const [catalog, setCatalog] = useState<QuestionCatalogWithQuestions | null>(
    null
  );
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [isNewCatalog, setIsNewCatalog] = useState(false);

  // Form state
  const [catalogName, setCatalogName] = useState('');
  const [catalogDescription, setCatalogDescription] = useState('');
  const [rawText, setRawText] = useState('');
  const [parsedQuestions, setParsedQuestions] = useState<ParsedQuestion[]>([]);

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [currentStep, setCurrentStep] = useState<
    'input' | 'parsed' | 'translated' | 'saved'
  >('input');
  const [translationProgress, setTranslationProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Image upload state
  const [uploadingImages, setUploadingImages] = useState<
    Record<string, boolean>
  >({});
  const [imageUploadProgress, setImageUploadProgress] = useState<
    Record<string, number>
  >({});

  // Question editing state
  const [isEditingQuestions, setIsEditingQuestions] = useState(false);
  const [currentEditIndex, setCurrentEditIndex] = useState(0);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [isSavingQuestion, setIsSavingQuestion] = useState(false);

  // Redirect if not authorized
  useEffect(() => {
    if (!isLoading && (!user || !canCreateArticle)) {
      navigate('/sign-in');
    }
  }, [user, isLoading, canCreateArticle, navigate]);

  // Load catalog data for editing
  useEffect(() => {
    if (!catalogId || isLoading) return;

    if (catalogId === 'new') {
      setIsNewCatalog(true);
      setLoadingCatalog(false);
      return;
    }

    const loadCatalog = async () => {
      try {
        setLoadingCatalog(true);
        const fetchedCatalog =
          await fetchQuestionCatalogWithQuestions(catalogId);
        if (fetchedCatalog) {
          setCatalog(fetchedCatalog);
          setCatalogName(fetchedCatalog.name);
          setCatalogDescription(fetchedCatalog.description || '');
          if (fetchedCatalog.questions.length > 0) {
            setCurrentStep('saved');
          }
        } else {
          navigate('/questions');
        }
      } catch (error) {
        console.error('Error loading question catalog:', error);
        navigate('/questions');
      } finally {
        setLoadingCatalog(false);
      }
    };

    loadCatalog();
  }, [catalogId, isLoading, navigate]);

  const handleParseText = async () => {
    if (!rawText.trim()) {
      toast.error(t('questionEditor.enterTextToParse'));
      return;
    }

    setIsProcessing(true);
    try {
      const result = await parseQuestions({ text: rawText }, user?.id);
      if (result.questions.length > 0) {
        setParsedQuestions(result.questions);
        setCurrentStep('parsed');
        toast.success(
          t('questionEditor.parseSuccess', { count: result.questions.length })
        );
      } else {
        toast.error(t('questionEditor.noQuestionsParsed'));
      }
    } catch (error) {
      console.error('Error parsing questions:', error);
      toast.error(t('questionEditor.parseFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTranslateQuestions = async () => {
    if (parsedQuestions.length === 0) return;

    setIsTranslating(true);
    setTranslationProgress(0);

    try {
      const translatedQuestions: Question[] = [];

      for (let i = 0; i < parsedQuestions.length; i++) {
        const question = parsedQuestions[i];

        // Translate to English
        const englishResult = await translateQuestion(
          {
            germanQuestion: question.germanQuestion,
            germanAnswers: question.answers,
            targetLanguage: 'english',
          },
          user?.id
        );

        // Translate to Vietnamese
        const vietnameseResult = await translateQuestion(
          {
            germanQuestion: question.germanQuestion,
            germanAnswers: question.answers,
            targetLanguage: 'vietnamese',
          },
          user?.id
        );

        // Create question with answers (marking first answer as correct by default)
        const questionWithAnswers: Question = {
          id: `temp-${i}`,
          catalogId: 'temp',
          questionNumber: question.questionNumber,
          germanQuestion: question.germanQuestion,
          englishQuestion: englishResult.translatedQuestion,
          vietnameseQuestion: vietnameseResult.translatedQuestion,
          imageUrl: null,
          stateSpecificFor: null,
          answers: question.answers.map((answer, idx) => ({
            id: `temp-${i}-${idx}`,
            answerIndex: idx + 1,
            germanAnswer: answer,
            englishAnswer: englishResult.translatedAnswers[idx] || answer,
            vietnameseAnswer: vietnameseResult.translatedAnswers[idx] || answer,
            isCorrect: idx === 0, // First answer marked as correct by default
          })),
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        translatedQuestions.push(questionWithAnswers);
        setTranslationProgress(((i + 1) / parsedQuestions.length) * 100);
      }

      // Update catalog with translated questions
      const updatedCatalog: QuestionCatalogWithQuestions = {
        id: catalog?.id || 'temp',
        name: catalogName,
        description: catalogDescription,
        totalQuestions: translatedQuestions.length,
        stateSpecificQuestions: 0,
        userId: user?.id,
        createdAt: catalog?.createdAt || new Date(),
        updatedAt: new Date(),
        questions: translatedQuestions,
      };

      setCatalog(updatedCatalog);
      setCurrentStep('translated');
      toast.success(t('questionEditor.translateSuccess'));
    } catch (error) {
      console.error('Error translating questions:', error);
      toast.error(t('questionEditor.translateFailed'));
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSaveCatalog = async () => {
    if (!catalog || !catalogName.trim()) {
      toast.error(t('questionEditor.provideCatalogName'));
      return;
    }

    setIsSaving(true);
    try {
      if (!user?.id) {
        toast.error(t('questionEditor.userNotAuthenticated'));
        return;
      }

      const savedCatalog = await saveQuestionCatalog(user.id, {
        name: catalogName,
        description: catalogDescription,
        totalQuestions: catalog.questions.length,
        stateSpecificQuestions: 0,
        questions: catalog.questions,
      });

      setCurrentStep('saved');
      toast.success(t('questionEditor.catalogSaveSuccess'));

      // Redirect to the saved catalog
      navigate(`/questions/${savedCatalog.id}/edit`);
    } catch (error) {
      console.error('Error saving catalog:', error);
      toast.error(t('questionEditor.catalogSaveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const toggleCorrectAnswer = (questionIdx: number, answerIdx: number) => {
    if (!catalog) return;

    const updatedCatalog = { ...catalog };
    const question = updatedCatalog.questions[questionIdx];

    // Set all answers to incorrect, then set the selected one as correct
    question.answers.forEach((answer, idx) => {
      answer.isCorrect = idx === answerIdx;
    });

    setCatalog(updatedCatalog);
  };

  const handleImageUpload = async (questionIdx: number, file: File) => {
    if (!catalog) return;

    setUploadingImages(prev => ({ ...prev, [questionIdx]: true }));
    setImageUploadProgress(prev => ({ ...prev, [questionIdx]: 0 }));

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setImageUploadProgress(prev => ({
          ...prev,
          [questionIdx]: Math.min(prev[questionIdx] + 10, 90),
        }));
      }, 100);

      const uploadResult = await uploadQuestionImageToCloudinary(file);

      clearInterval(progressInterval);
      setImageUploadProgress(prev => ({ ...prev, [questionIdx]: 100 }));

      // Update the catalog with the new image URL
      const updatedCatalog = updateQuestionImageInCatalog(
        catalog,
        questionIdx,
        uploadResult.secure_url
      );
      setCatalog(updatedCatalog);

      toast.success(t('questionEditor.imageUploadSuccess'));
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : t('questionEditor.imageUploadFailed')
      );
    } finally {
      setUploadingImages(prev => ({ ...prev, [questionIdx]: false }));
      setTimeout(() => {
        setImageUploadProgress(prev => ({ ...prev, [questionIdx]: 0 }));
      }, 2000);
    }
  };

  const handleRemoveImage = (questionIdx: number) => {
    if (!catalog) return;

    const updatedCatalog = updateQuestionImageInCatalog(
      catalog,
      questionIdx,
      null
    );
    setCatalog(updatedCatalog);
    toast.success(t('questionEditor.imageRemoved'));
  };

  const handleImageFileSelect = (
    questionIdx: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(questionIdx, file);
    }
    // Reset the input value so the same file can be selected again if needed
    event.target.value = '';
  };

  // Question editing handlers
  const handleStartEditing = () => {
    if (catalog && catalog.questions.length > 0) {
      setIsEditingQuestions(true);
      setCurrentEditIndex(0);
      setEditingQuestion({ ...catalog.questions[0] });
    }
  };

  const handleStopEditing = () => {
    setIsEditingQuestions(false);
    setCurrentEditIndex(0);
    setEditingQuestion(null);
  };

  const handleNextQuestion = () => {
    if (catalog && currentEditIndex < catalog.questions.length - 1) {
      const nextIndex = currentEditIndex + 1;
      setCurrentEditIndex(nextIndex);
      setEditingQuestion({ ...catalog.questions[nextIndex] });
      // Clear any upload states from previous question
      const prevUploadKey = `editing-${currentEditIndex}`;
      setUploadingImages(prev => ({ ...prev, [prevUploadKey]: false }));
      setImageUploadProgress(prev => ({ ...prev, [prevUploadKey]: 0 }));
    }
  };

  const handlePrevQuestion = () => {
    if (catalog && currentEditIndex > 0) {
      const prevIndex = currentEditIndex - 1;
      setCurrentEditIndex(prevIndex);
      setEditingQuestion({ ...catalog.questions[prevIndex] });
      // Clear any upload states from previous question
      const nextUploadKey = `editing-${currentEditIndex}`;
      setUploadingImages(prev => ({ ...prev, [nextUploadKey]: false }));
      setImageUploadProgress(prev => ({ ...prev, [nextUploadKey]: 0 }));
    }
  };

  const handleGoToQuestion = (index: number) => {
    if (catalog && index >= 0 && index < catalog.questions.length) {
      // Clear any upload states from current question
      const currentUploadKey = `editing-${currentEditIndex}`;
      setUploadingImages(prev => ({ ...prev, [currentUploadKey]: false }));
      setImageUploadProgress(prev => ({ ...prev, [currentUploadKey]: 0 }));

      setCurrentEditIndex(index);
      setEditingQuestion({ ...catalog.questions[index] });
    }
  };

  const handleUpdateQuestionField = (field: keyof Question, value: string) => {
    if (editingQuestion) {
      setEditingQuestion({
        ...editingQuestion,
        [field]: value,
      });
    }
  };

  const handleUpdateAnswerField = (
    answerIndex: number,
    field: keyof QuestionAnswer,
    value: string | boolean
  ) => {
    if (editingQuestion) {
      const updatedAnswers = editingQuestion.answers.map((answer, idx) =>
        idx === answerIndex ? { ...answer, [field]: value } : answer
      );
      setEditingQuestion({
        ...editingQuestion,
        answers: updatedAnswers,
      });
    }
  };

  const handleSaveCurrentQuestion = async () => {
    if (!editingQuestion || !catalog) return;

    try {
      setIsSavingQuestion(true);

      // Update question translations
      await updateQuestionTranslations(
        editingQuestion.id,
        editingQuestion.englishQuestion || undefined,
        editingQuestion.vietnameseQuestion || undefined
      );

      // Update answer translations
      for (const answer of editingQuestion.answers) {
        await updateAnswerTranslations(
          answer.id,
          answer.englishAnswer || undefined,
          answer.vietnameseAnswer || undefined
        );
      }

      // Update local state
      const updatedQuestions = catalog.questions.map((q, idx) =>
        idx === currentEditIndex ? editingQuestion : q
      );
      setCatalog({
        ...catalog,
        questions: updatedQuestions,
      });

      toast.success(t('questionEditor.questionUpdateSuccess'));
    } catch (error) {
      console.error('Error saving question:', error);
      toast.error(t('questionEditor.questionUpdateFailed'));
    } finally {
      setIsSavingQuestion(false);
    }
  };

  // Image editing handlers for the editing interface
  const handleEditingImageUpload = async (file: File) => {
    if (!editingQuestion || !catalog) return;

    const uploadKey = `editing-${currentEditIndex}`;
    setUploadingImages(prev => ({ ...prev, [uploadKey]: true }));
    setImageUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));

    try {
      const uploadResult = await uploadQuestionImageToCloudinary(file);

      setImageUploadProgress(prev => ({ ...prev, [uploadKey]: 100 }));

      // Update the database since this is a saved question
      await updateQuestionImage(editingQuestion.id, uploadResult.secure_url);

      // Update local editing state
      const updatedEditingQuestion = {
        ...editingQuestion,
        imageUrl: uploadResult.secure_url,
      };
      setEditingQuestion(updatedEditingQuestion);

      // Update catalog state
      const updatedQuestions = catalog.questions.map((q, idx) =>
        idx === currentEditIndex ? updatedEditingQuestion : q
      );
      setCatalog({
        ...catalog,
        questions: updatedQuestions,
      });

      toast.success(t('questionEditor.imageUploadSuccess'));
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error(t('questionEditor.imageUploadFailed'));
    } finally {
      setUploadingImages(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleEditingRemoveImage = async () => {
    if (!editingQuestion || !catalog) return;

    try {
      // Update the database to remove image
      await updateQuestionImage(editingQuestion.id, '');

      // Update local editing state
      const updatedEditingQuestion = {
        ...editingQuestion,
        imageUrl: null,
      };
      setEditingQuestion(updatedEditingQuestion);

      // Update catalog state
      const updatedQuestions = catalog.questions.map((q, idx) =>
        idx === currentEditIndex ? updatedEditingQuestion : q
      );
      setCatalog({
        ...catalog,
        questions: updatedQuestions,
      });

      toast.success(t('questionEditor.imageRemovedSuccess'));
    } catch (error) {
      console.error('Failed to remove image:', error);
      toast.error(t('questionEditor.imageRemoveFailed'));
    }
  };

  const handleEditingImageFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleEditingImageUpload(file);
    }
    // Reset the input value so the same file can be selected again if needed
    event.target.value = '';
  };

  if (isLoading || loadingCatalog) {
    return (
      <div className="min-h-screen bg-gradient-hero">
        <div className="container max-w-6xl mx-auto p-6">
          <Skeleton className="h-12 w-1/2 mb-8" />
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-4 w-2/3" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero">
      <div className="container max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="outline"
            onClick={() => navigate('/questions')}
            className="bg-white/10 text-white border-white/20 hover:bg-white/20"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('questionEditor.back')}
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-primary-foreground">
              {isNewCatalog
                ? t('questionEditor.createQuestionCatalog')
                : t('questionEditor.editQuestionCatalog', {
                    name: catalog?.name || 'Question Catalog',
                  })}
            </h1>
            <p className="text-primary-foreground/90">
              {t('questionEditor.addAndManage')}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Basic Information */}
          <Card className="bg-white/95 backdrop-blur-sm border-0">
            <CardHeader>
              <CardTitle>{t('questionEditor.catalogInformation')}</CardTitle>
              <CardDescription>
                {t('questionEditor.catalogDetailsDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="catalog-name">
                    {t('common.catalogName')}
                  </Label>
                  <Input
                    id="catalog-name"
                    value={catalogName}
                    onChange={e => setCatalogName(e.target.value)}
                    placeholder={t('questionEditor.catalogPlaceholder')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="catalog-description">
                    {t('questionEditor.description')}
                  </Label>
                  <Input
                    id="catalog-description"
                    value={catalogDescription}
                    onChange={e => setCatalogDescription(e.target.value)}
                    placeholder={t('questionEditor.catalogDescription')}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Step 1: Import Questions */}
          {currentStep === 'input' && (
            <Card className="bg-white/95 backdrop-blur-sm border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  {t('questionEditor.importQuestions')}
                </CardTitle>
                <CardDescription>
                  {t('questionEditor.pasteQuestionsDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="raw-text">
                    {t('questionEditor.questionText')}
                  </Label>
                  <Textarea
                    id="raw-text"
                    value={rawText}
                    onChange={e => setRawText(e.target.value)}
                    placeholder={t('questionEditor.questionsPlaceholder')}
                    rows={12}
                    className="font-mono text-sm"
                  />
                </div>
                <Button
                  onClick={handleParseText}
                  disabled={isProcessing || !rawText.trim()}
                  className="bg-learning-primary hover:bg-learning-primary/90"
                >
                  {isProcessing ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Parsing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Parse Questions
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Review Parsed Questions */}
          {currentStep === 'parsed' && (
            <Card className="bg-white/95 backdrop-blur-sm border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  {t('questionEditor.parsedQuestions', {
                    count: parsedQuestions.length,
                  })}
                </CardTitle>
                <CardDescription>
                  {t('questionEditor.reviewParsedDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="max-h-96 overflow-y-auto space-y-4">
                  {parsedQuestions.map((question, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">
                          {t('questionEditor.questionNumber', {
                            number: question.questionNumber,
                          })}
                        </Badge>
                      </div>
                      <p className="font-medium mb-3">
                        {question.germanQuestion}
                      </p>
                      <div className="space-y-1">
                        {question.answers.map((answer, ansIdx) => (
                          <div
                            key={ansIdx}
                            className="text-sm text-muted-foreground"
                          >
                            {String.fromCharCode(97 + ansIdx)}) {answer}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleTranslateQuestions}
                    disabled={isTranslating}
                    className="bg-learning-primary hover:bg-learning-primary/90"
                  >
                    {isTranslating ? (
                      <>
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                        {t('questionEditor.translating')}
                      </>
                    ) : (
                      <>
                        <Languages className="h-4 w-4 mr-2" />
                        {t('questionEditor.translateAllQuestions')}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep('input')}
                  >
                    {t('questionEditor.backToEdit')}
                  </Button>
                </div>

                {isTranslating && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{t('questionEditor.translationProgress')}</span>
                      <span>{Math.round(translationProgress)}%</span>
                    </div>
                    <Progress value={translationProgress} className="w-full" />
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 3: Review Translated Questions */}
          {currentStep === 'translated' && catalog && (
            <Card className="bg-white/95 backdrop-blur-sm border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Languages className="h-5 w-5 text-blue-600" />
                  {t('questionEditor.translatedQuestions', {
                    count: catalog.questions.length,
                  })}
                </CardTitle>
                <CardDescription>
                  {t('questionEditor.reviewTranslationsDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="max-h-96 overflow-y-auto space-y-6">
                  {catalog.questions.map((question, qIdx) => (
                    <div key={qIdx} className="p-4 border rounded-lg space-y-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">
                          {t('questionEditor.questionNumber', {
                            number: question.questionNumber,
                          })}
                        </Badge>
                      </div>

                      {/* Question Text */}
                      <div className="space-y-2">
                        <div className="font-medium text-foreground">
                          🇩🇪 {question.germanQuestion}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          🇬🇧 {question.englishQuestion}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          🇻🇳 {question.vietnameseQuestion}
                        </div>
                      </div>

                      <Separator />

                      {/* Question Image */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">
                            Question Image (Optional)
                          </Label>
                          {question.imageUrl ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveImage(qIdx)}
                              className="text-red-600 hover:text-red-700 hover:border-red-300"
                            >
                              <X className="h-4 w-4 mr-2" />
                              {t('questionEditor.removeImage')}
                            </Button>
                          ) : (
                            <div className="relative">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={e => handleImageFileSelect(qIdx, e)}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                id={`image-upload-${qIdx}`}
                                disabled={uploadingImages[qIdx]}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={uploadingImages[qIdx]}
                                asChild
                              >
                                <label
                                  htmlFor={`image-upload-${qIdx}`}
                                  className="cursor-pointer"
                                >
                                  {uploadingImages[qIdx] ? (
                                    <>
                                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                                      {t('questionEditor.uploading')}
                                    </>
                                  ) : (
                                    <>
                                      <Camera className="h-4 w-4 mr-2" />
                                      {t('questionEditor.addImage')}
                                    </>
                                  )}
                                </label>
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Image Preview */}
                        {question.imageUrl && (
                          <div className="relative">
                            <img
                              src={question.imageUrl}
                              alt={`Question ${question.questionNumber}`}
                              className="max-w-full h-auto max-h-48 rounded-lg border object-contain mx-auto"
                            />
                          </div>
                        )}

                        {/* Upload Progress */}
                        {uploadingImages[qIdx] &&
                          imageUploadProgress[qIdx] > 0 && (
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span>
                                  {t('questionEditor.uploadingImage')}
                                </span>
                                <span>
                                  {Math.round(imageUploadProgress[qIdx])}%
                                </span>
                              </div>
                              <Progress
                                value={imageUploadProgress[qIdx]}
                                className="w-full"
                              />
                            </div>
                          )}
                      </div>

                      <Separator />

                      {/* Answers */}
                      <div className="space-y-3">
                        {question.answers.map((answer, aIdx) => (
                          <div
                            key={aIdx}
                            className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                              answer.isCorrect
                                ? 'border-green-500 bg-green-50'
                                : 'border-border hover:border-learning-primary/40'
                            }`}
                            onClick={() => toggleCorrectAnswer(qIdx, aIdx)}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <Badge
                                variant={
                                  answer.isCorrect ? 'default' : 'outline'
                                }
                              >
                                {String.fromCharCode(97 + aIdx)}
                              </Badge>
                              {answer.isCorrect && (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              )}
                            </div>
                            <div className="space-y-1">
                              <div className="text-sm font-medium">
                                🇩🇪 {answer.germanAnswer}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                🇬🇧 {answer.englishAnswer}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                🇻🇳 {answer.vietnameseAnswer}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleSaveCatalog}
                    disabled={isSaving}
                    className="bg-learning-primary hover:bg-learning-primary/90"
                  >
                    {isSaving ? (
                      <>
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {t('questionEditor.saveQuestionCatalog')}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleTranslateQuestions}
                    disabled={isSaving}
                  >
                    Re-translate
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Saved */}
          {currentStep === 'saved' && catalog && (
            <Card className="bg-white/95 backdrop-blur-sm border-0">
              <CardContent className="p-8 text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold mb-2">
                  {t('questionEditor.questionCatalogSaved')}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {t('questionEditor.catalogSavedDesc', {
                    name: catalog.name,
                    count: catalog.questions.length,
                  })}
                </p>
                <div className="flex gap-3 justify-center">
                  <Button
                    onClick={() =>
                      navigate(`/questions/${catalog.id}/practice`)
                    }
                    className="bg-learning-primary hover:bg-learning-primary/90"
                  >
                    Start Practice
                  </Button>
                  <Button
                    onClick={handleStartEditing}
                    variant="outline"
                    className="border-learning-primary text-learning-primary hover:bg-learning-primary hover:text-white"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit Questions
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/questions')}
                  >
                    View All Catalogs
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Question Editor Interface */}
          {isEditingQuestions && editingQuestion && catalog && (
            <Card className="bg-white/95 backdrop-blur-sm border-0">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Edit2 className="h-5 w-5 text-learning-primary" />
                      {t('questionEditor.editingQuestion', {
                        current: currentEditIndex + 1,
                        total: catalog.questions.length,
                      })}
                    </CardTitle>
                    <CardDescription>
                      {t('questionEditor.editQuestionDesc')}
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleStopEditing}
                    className="text-muted-foreground"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Stop Editing
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Question Navigation */}
                <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevQuestion}
                    disabled={currentEditIndex === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      {t('common.question')}:
                    </span>
                    <select
                      value={currentEditIndex}
                      onChange={e =>
                        handleGoToQuestion(parseInt(e.target.value))
                      }
                      className="px-3 py-1 border rounded text-sm"
                    >
                      {catalog.questions.map((_, idx) => (
                        <option key={idx} value={idx}>
                          {idx + 1}
                        </option>
                      ))}
                    </select>
                    <Badge variant="outline">
                      #{editingQuestion.questionNumber}
                    </Badge>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextQuestion}
                    disabled={currentEditIndex === catalog.questions.length - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>

                {/* Question Text Editing */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="german-question">
                      {t('questionEditor.germanQuestion')}
                    </Label>
                    <Textarea
                      id="german-question"
                      value={editingQuestion.germanQuestion}
                      onChange={e =>
                        handleUpdateQuestionField(
                          'germanQuestion',
                          e.target.value
                        )
                      }
                      className="min-h-[60px]"
                      placeholder={t(
                        'questionEditor.germanQuestionPlaceholder'
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="english-question">
                      {t('questionEditor.englishTranslation')}
                    </Label>
                    <Textarea
                      id="english-question"
                      value={editingQuestion.englishQuestion || ''}
                      onChange={e =>
                        handleUpdateQuestionField(
                          'englishQuestion',
                          e.target.value
                        )
                      }
                      className="min-h-[60px]"
                      placeholder={t(
                        'questionEditor.englishTranslationPlaceholder'
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vietnamese-question">
                      {t('questionEditor.vietnameseTranslation')}
                    </Label>
                    <Textarea
                      id="vietnamese-question"
                      value={editingQuestion.vietnameseQuestion || ''}
                      onChange={e =>
                        handleUpdateQuestionField(
                          'vietnameseQuestion',
                          e.target.value
                        )
                      }
                      className="min-h-[60px]"
                      placeholder={t(
                        'questionEditor.vietnameseTranslationPlaceholder'
                      )}
                    />
                  </div>
                </div>

                <Separator />

                {/* Question Image Editing */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">
                      {t('questionEditor.questionImageOptional')}
                    </Label>
                    {editingQuestion.imageUrl ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleEditingRemoveImage}
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                      >
                        <X className="h-4 w-4 mr-2" />
                        {t('questionEditor.removeImage')}
                      </Button>
                    ) : (
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleEditingImageFileSelect}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          id="editing-image-upload"
                          disabled={
                            uploadingImages[`editing-${currentEditIndex}`]
                          }
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={
                            uploadingImages[`editing-${currentEditIndex}`]
                          }
                          asChild
                        >
                          <label
                            htmlFor="editing-image-upload"
                            className="cursor-pointer"
                          >
                            {uploadingImages[`editing-${currentEditIndex}`] ? (
                              <>
                                <Loader className="h-4 w-4 mr-2 animate-spin" />
                                {t('questionEditor.uploading')}
                              </>
                            ) : (
                              <>
                                <Camera className="h-4 w-4 mr-2" />
                                {t('questionEditor.addImage')}
                              </>
                            )}
                          </label>
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Image Preview */}
                  {editingQuestion.imageUrl && (
                    <div className="relative">
                      <img
                        src={editingQuestion.imageUrl}
                        alt={`Question ${editingQuestion.questionNumber}`}
                        className="max-w-full h-auto max-h-48 rounded-lg border object-contain mx-auto"
                      />
                    </div>
                  )}

                  {/* Upload Progress */}
                  {uploadingImages[`editing-${currentEditIndex}`] &&
                    imageUploadProgress[`editing-${currentEditIndex}`] > 0 && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>{t('questionEditor.uploadingImage')}</span>
                          <span>
                            {Math.round(
                              imageUploadProgress[`editing-${currentEditIndex}`]
                            )}
                            %
                          </span>
                        </div>
                        <Progress
                          value={
                            imageUploadProgress[`editing-${currentEditIndex}`]
                          }
                          className="w-full"
                        />
                      </div>
                    )}
                </div>

                <Separator />

                {/* Answers Editing */}
                <div className="space-y-4">
                  <Label className="text-base font-semibold">
                    {t('questionEditor.answers')}
                  </Label>
                  {editingQuestion.answers.map((answer, ansIdx) => (
                    <div
                      key={ansIdx}
                      className={`p-4 border rounded-lg space-y-3 ${
                        answer.isCorrect
                          ? 'border-green-500 bg-green-50'
                          : 'border-border'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Badge
                          variant={answer.isCorrect ? 'default' : 'outline'}
                        >
                          {String.fromCharCode(97 + ansIdx)}
                        </Badge>
                        <Label className="text-sm">
                          <input
                            type="checkbox"
                            checked={answer.isCorrect}
                            onChange={e =>
                              handleUpdateAnswerField(
                                ansIdx,
                                'isCorrect',
                                e.target.checked
                              )
                            }
                            className="mr-2"
                          />
                          Correct Answer
                        </Label>
                      </div>

                      <div className="space-y-2">
                        <Input
                          placeholder={t(
                            'questionEditor.germanAnswerPlaceholder'
                          )}
                          value={answer.germanAnswer}
                          onChange={e =>
                            handleUpdateAnswerField(
                              ansIdx,
                              'germanAnswer',
                              e.target.value
                            )
                          }
                        />
                        <Input
                          placeholder={t(
                            'questionEditor.englishAnswerPlaceholder'
                          )}
                          value={answer.englishAnswer || ''}
                          onChange={e =>
                            handleUpdateAnswerField(
                              ansIdx,
                              'englishAnswer',
                              e.target.value
                            )
                          }
                        />
                        <Input
                          placeholder={t(
                            'questionEditor.vietnameseAnswerPlaceholder'
                          )}
                          value={answer.vietnameseAnswer || ''}
                          onChange={e =>
                            handleUpdateAnswerField(
                              ansIdx,
                              'vietnameseAnswer',
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Save Button */}
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleSaveCurrentQuestion}
                    disabled={isSavingQuestion}
                    className="bg-learning-primary hover:bg-learning-primary/90"
                  >
                    {isSavingQuestion ? (
                      <>
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {t('questionEditor.saveQuestion')}
                      </>
                    )}
                  </Button>

                  {currentEditIndex < catalog.questions.length - 1 && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        handleSaveCurrentQuestion().then(() => {
                          handleNextQuestion();
                        });
                      }}
                      disabled={isSavingQuestion}
                    >
                      {t('questionEditor.saveNext')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionEditor;
